import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { hasPermission } from "hooks/usePermission";
import CustomButton from "components/common/Button";
import CustomTooltip from "components/common/Tooltip";
import SectionLoader from "components/common/SectionLoader";
import { AlertType } from "components/common/Snackbar";

import { GetCatalog } from "store/actions/Rbac";
import { showSnackbar } from "store/actions/Utility";

import {
  createFeature, updateFeature, deleteFeature,
  createPermission, updatePermission, deletePermission,
} from "services/rbac";
import FeatureDialog from "./FeatureDialog";
import PermissionDialog from "./PermissionDialog";
import "./FeatureCatalog.scss";

/**
 * FeatureCatalog — edge-only admin screen for managing RBAC features and their permissions.
 *
 * Catalog shape (each item):
 *   { featureId, key, name, description, displayOrder,
 *     permissions: [{ permissionId, key, action, name, userTypes, customerGrantable }] }
 *
 * ID derivation (client-side, mirrors catalog convention):
 *   Feature_ID    = "feat_" + Key
 *   Permission_ID = "perm_" + Key  (Key = "<feature.key>.<action>")
 *   Action        = last segment of permission Key after the final "."
 */
export default function FeatureCatalog() {
  const dispatch = useDispatch();
  const { catalog, loadingCatalog } = useSelector((s) => s.Rbac || {});

  const canManageFeatures = hasPermission("features.manage");

  // Feature dialog state
  const [featureDialog, setFeatureDialog] = useState(null); // { mode, feature } | null
  // Permission dialog state
  const [permDialog, setPermDialog] = useState(null); // { mode, permission, feature } | null

  const reload = () => {
    dispatch(GetCatalog());
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Feature handlers ----

  const handleFeatureSubmit = async (payload) => {
    try {
      const { mode, featureId, Key, Name, Description, Display_Order, Feature_ID } = payload;
      const res =
        mode === "create"
          ? await createFeature({ Feature_ID, Key, Name, Description, Display_Order })
          : await updateFeature(featureId, { Name, Description, Display_Order });

      if (res && res.ok) {
        dispatch(
          showSnackbar({
            type: AlertType.success,
            message: mode === "create" ? "Feature created" : "Feature updated",
            dispatch,
          })
        );
        setFeatureDialog(null);
        reload();
      } else {
        dispatch(
          showSnackbar({ type: AlertType.error, message: res?.payload?.message || "Action failed", dispatch })
        );
      }
    } catch {
      dispatch(showSnackbar({ type: AlertType.error, message: "Action failed", dispatch }));
    }
  };

  const handleFeatureDelete = async (feature) => {
    try {
      const res = await deleteFeature(feature.featureId);
      if (res && res.ok) {
        dispatch(showSnackbar({ type: AlertType.success, message: "Feature deleted", dispatch }));
        reload();
      } else {
        dispatch(
          showSnackbar({
            type: AlertType.error,
            message: res?.payload?.message || "Cannot delete this feature",
            dispatch,
          })
        );
      }
    } catch {
      dispatch(showSnackbar({ type: AlertType.error, message: "Cannot delete this feature", dispatch }));
    }
  };

  // ---- Permission handlers ----

  const handlePermissionSubmit = async (payload) => {
    try {
      const {
        mode, permissionId, Permission_ID, Feature_ID, Key, Action, Name,
        Applicable_User_Types, Customer_Grantable,
      } = payload;
      const res =
        mode === "create"
          ? await createPermission({
              Permission_ID, Feature_ID, Key, Action, Name,
              Applicable_User_Types, Customer_Grantable,
            })
          : await updatePermission(permissionId, {
              Name, Applicable_User_Types, Customer_Grantable,
            });

      if (res && res.ok) {
        dispatch(
          showSnackbar({
            type: AlertType.success,
            message: mode === "create" ? "Permission created" : "Permission updated",
            dispatch,
          })
        );
        setPermDialog(null);
        reload();
      } else {
        dispatch(
          showSnackbar({ type: AlertType.error, message: res?.payload?.message || "Action failed", dispatch })
        );
      }
    } catch {
      dispatch(showSnackbar({ type: AlertType.error, message: "Action failed", dispatch }));
    }
  };

  const handlePermissionDelete = async (permission) => {
    try {
      const res = await deletePermission(permission.permissionId);
      if (res && res.ok) {
        dispatch(showSnackbar({ type: AlertType.success, message: "Permission deleted", dispatch }));
        reload();
      } else {
        dispatch(
          showSnackbar({
            type: AlertType.error,
            message: res?.payload?.message || "Cannot delete this permission",
            dispatch,
          })
        );
      }
    } catch {
      dispatch(showSnackbar({ type: AlertType.error, message: "Cannot delete this permission", dispatch }));
    }
  };

  return (
    <div className="feature-catalog">
      {/* Page header */}
      <div className="feature-catalog__header">
        <div className="heading-h3-semibold">Feature Catalog</div>
        {canManageFeatures && (
          <CustomButton
            label="Add feature"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setFeatureDialog({ mode: "create", feature: null })}
          />
        )}
      </div>

      {loadingCatalog ? (
        <SectionLoader />
      ) : (
        <div className="feature-catalog__list">
          {(catalog || []).length === 0 && (
            <p className="feature-catalog__empty para-body-m-regular">No features defined yet.</p>
          )}

          {(catalog || []).map((feature) => (
            <div key={feature.featureId} className="feature-catalog__card">
              {/* Feature header row */}
              <div className="feature-catalog__feature-header">
                <div className="feature-catalog__feature-title">
                  <span className="heading-h4-semibold">{feature.name}</span>
                  <span className="feature-catalog__muted para-body-s-regular">({feature.key})</span>
                  {feature.displayOrder != null && (
                    <span className="feature-catalog__muted para-body-s-regular">
                      order: {feature.displayOrder}
                    </span>
                  )}
                </div>

                <div className="feature-catalog__actions">
                  {canManageFeatures && (
                    <CustomButton
                      label="Add permission"
                      variant="text"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setPermDialog({ mode: "create", permission: null, feature })}
                    />
                  )}
                  {canManageFeatures && (
                    <CustomTooltip content="Edit feature">
                      <IconButton
                        aria-label="Edit feature"
                        size="small"
                        className="feature-catalog__icon-action"
                        onClick={() => setFeatureDialog({ mode: "edit", feature })}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </CustomTooltip>
                  )}
                  {canManageFeatures && (
                    <CustomTooltip content="Delete feature">
                      <IconButton
                        aria-label="Delete feature"
                        size="small"
                        className="feature-catalog__icon-action"
                        onClick={() => handleFeatureDelete(feature)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </CustomTooltip>
                  )}
                </div>
              </div>

              {/* Permissions list */}
              {(feature.permissions || []).length === 0 ? (
                <p className="feature-catalog__no-permissions para-body-s-regular">No permissions.</p>
              ) : (
                <div className="feature-catalog__permissions">
                  {feature.permissions.map((perm) => (
                    <div key={perm.permissionId} className="feature-catalog__permission-row">
                      <div className="feature-catalog__permission-info">
                        <div className="feature-catalog__permission-title">
                          <span className="para-body-m-medium">{perm.name}</span>
                          <span className="feature-catalog__muted para-body-s-regular">{perm.key}</span>
                        </div>
                        {[
                          perm.userTypes?.length ? `Users: ${perm.userTypes.join(", ")}` : null,
                          perm.customerGrantable ? "Customer-grantable" : null,
                        ].filter(Boolean).length > 0 && (
                          <div className="feature-catalog__muted para-body-s-regular">
                            {[
                              perm.userTypes?.length ? `Users: ${perm.userTypes.join(", ")}` : null,
                              perm.customerGrantable ? "Customer-grantable" : null,
                            ]
                              .filter(Boolean)
                              .join("  •  ")}
                          </div>
                        )}
                      </div>

                      <div className="feature-catalog__actions">
                        {canManageFeatures && (
                          <CustomTooltip content="Edit permission">
                            <IconButton
                              aria-label="Edit permission"
                              size="small"
                              className="feature-catalog__icon-action"
                              onClick={() => setPermDialog({ mode: "edit", permission: perm, feature })}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </CustomTooltip>
                        )}
                        {canManageFeatures && (
                          <CustomTooltip content="Delete permission">
                            <IconButton
                              aria-label="Delete permission"
                              size="small"
                              className="feature-catalog__icon-action"
                              onClick={() => handlePermissionDelete(perm)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </CustomTooltip>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Feature dialog */}
      <FeatureDialog
        open={!!featureDialog}
        mode={featureDialog?.mode}
        feature={featureDialog?.feature}
        onClose={() => setFeatureDialog(null)}
        onSubmit={handleFeatureSubmit}
      />

      {/* Permission dialog */}
      <PermissionDialog
        open={!!permDialog}
        mode={permDialog?.mode}
        permission={permDialog?.permission}
        feature={permDialog?.feature}
        onClose={() => setPermDialog(null)}
        onSubmit={handlePermissionSubmit}
      />
    </div>
  );
}
